import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { TeamDto } from '@signalvote/shared';
import type { CurrentRead } from '@signalvote/data';

interface AssignToTeamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teams: CurrentRead<TeamDto[]>;
  selectedUserIds: string[];
  onAssign: (teamIds: string[]) => void;
  isLoading?: boolean;
}

export function AssignToTeamDialog({
  open,
  onOpenChange,
  teams,
  selectedUserIds,
  onAssign,
  isLoading = false,
}: AssignToTeamDialogProps) {
  const [checkedTeamIds, setCheckedTeamIds] = useState<string[]>([]);

  // Clear selections when dialog closes
  useEffect(() => {
    if (!open) {
      setCheckedTeamIds([]);
    }
  }, [open]);

  const handleToggleTeam = (teamId: string, checked: boolean) => {
    setCheckedTeamIds((prev) =>
      checked ? [...prev, teamId] : prev.filter((id) => id !== teamId)
    );
  };

  const handleAssign = () => {
    if (checkedTeamIds.length === 0) return;
    onAssign(checkedTeamIds);
  };

  const userCount = selectedUserIds.length;
  const teamCount = checkedTeamIds.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Assign to Team</DialogTitle>
          <DialogDescription>
            Select one or more teams to assign{' '}
            {userCount === 1
              ? 'the selected person'
              : `${userCount} selected people`}{' '}
            to.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {teams.status !== 'ready' ? (
            <p role="status" className="text-sm text-muted-foreground text-center py-6">
              {teams.status === 'failed'
                ? "Couldn't load teams. Try again shortly."
                : teams.status === 'offline'
                  ? "You're offline, so teams can't load."
                  : 'Loading teams…'}
            </p>
          ) : teams.data.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No teams available. Create a team first.
            </p>
          ) : (
            <div className="space-y-1 max-h-[280px] overflow-y-auto">
              {teams.data.map((team) => (
                <label
                  key={team.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer hover:bg-accent transition-colors"
                >
                  <Checkbox
                    checked={checkedTeamIds.includes(team.id)}
                    onCheckedChange={(checked) =>
                      handleToggleTeam(team.id, checked === true)
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium truncate block">
                      {team.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {team.memberCount}{' '}
                      {team.memberCount === 1 ? 'member' : 'members'}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleAssign}
            disabled={teamCount === 0 || isLoading}
          >
            {isLoading && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            )}
            {teamCount === 0
              ? 'Assign'
              : `Assign to ${teamCount} team${teamCount === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
